import { Button, Divider, Flex, LoadingOverlay, Paper, ScrollArea, Text } from "@mantine/core";
import IconDeviceFloppy from "@tabler/icons-react/dist/esm/icons/IconDeviceFloppy.mjs";
import { Fragment, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useEmailConfigsQuery,
  useSaveRoleNotificationsMutation,
} from "../emailSettings/useEmailSettings.logic";
import RoleSection from "./components/RoleSection";
import { ROLES } from "./constants";

const buildDefaultValues = (data) => {
  const configs = {};
  (data || []).forEach((roleEntry) => {
    (roleEntry.configs || []).forEach((c) => {
      configs[c.id] = {
        period: c.period ?? "",
        count: c.count ?? "",
        countPerPeriod: c.countPerPeriod ?? "",
      };
    });
  });
  return { configs };
};

const RoleEmailNotifications = () => {
  const { t } = useTranslation();
  const { data: emailConfigsData, isLoading } = useEmailConfigsQuery();
  const saveMutation = useSaveRoleNotificationsMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields, isDirty },
  } = useForm({ defaultValues: { configs: {} } });

  useEffect(() => {
    if (emailConfigsData) reset(buildDefaultValues(emailConfigsData));
  }, [emailConfigsData, reset]);

  const configsByRole = useMemo(() => {
    const map = {};
    (emailConfigsData || []).forEach((entry) => {
      map[entry.role] = entry.configs || [];
    });
    return map;
  }, [emailConfigsData]);

  const onSubmit = (values) => {
    const dirtyIds = Object.keys(dirtyFields.configs || {});
    if (!dirtyIds.length) return;

    const allConfigs = (emailConfigsData || []).flatMap((e) => e.configs || []);
    const changed = dirtyIds
      .map((id) => {
        const original = allConfigs.find((c) => String(c.id) === String(id));
        if (!original) return null;
        const edited = values.configs[id];
        return {
          ...original,
          period: edited.period,
          count: edited.count,
          countPerPeriod: edited.countPerPeriod,
        };
      })
      .filter(Boolean);

    if (!changed.length) return;
    saveMutation.mutate(changed, {
      onSuccess: () => reset(values),
    });
  };

  return (
    <Paper pos="relative" withBorder radius="md" style={{ overflow: "hidden" }}>
      <LoadingOverlay visible={isLoading || saveMutation.isPending} overlayBlur={2} />
      <Flex justify="space-between" align="center" p="sm">
        <Text size="md">
          {t("settings.roleEmailNotifications")}
        </Text>
        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          disabled={!isDirty}
          loading={saveMutation.isPending}
        >
          SAVE
        </Button>
      </Flex>
      <Divider color="secondary.1" />
      <Paper bg="secondary.0" p="md" radius={0}>
        <ScrollArea>
          <Flex gap="lg" align="stretch">
            {ROLES.map((role, index) => (
              <Fragment key={role}>
                {index > 0 && <Divider orientation="vertical" color="secondary.1" />}
                <RoleSection
                  role={role}
                  configs={configsByRole[role] || []}
                  control={control}
                />
              </Fragment>
            ))}
          </Flex>
        </ScrollArea>
      </Paper>
    </Paper>
  );
};

export default RoleEmailNotifications;
